import { Id, Section } from "@dgs/core";
import { useFormikContext } from "formik";
import { FC } from "react";
import styled from "styled-components";
import {
	RegistrationFormSectionResource,
	RuleGuestAttributeResourceTypeDataField,
	RuleResource,
	TravelSectionSettingsResource,
} from "../../shared/types";
import { BlockRenderer } from "../BlockRenderer";
import { useIsAvailableSection } from "../provider/registrationFormProviderContext";
import { RegistrationCompanionSection } from "./companion/RegistrationCompanionSection";
import { RegistrationHotelSection } from "./hotel/RegistrationHotelSection";
import { RegistrationDefaultSection } from "./RegistrationDefaultSection";
import { RegistrationTravelSection } from "./travel/RegistrationTravelSection";

interface Props {
	section: RegistrationFormSectionResource;
	registrationFormId: Id;
	guestId: Id | null;
	stepId: number;
	validationRules: RuleResource<RuleGuestAttributeResourceTypeDataField>[];
	travelSectionSettings: TravelSectionSettingsResource | null;
}

const SectionWrapper = styled(Section)`
	gap: ${({ theme }) => theme.spacing(2)};
	padding: ${({ theme }) => theme.spacing(4)};
`;

export const RegistrationSection: FC<Props> = ({
	section,
	registrationFormId,
	guestId,
	stepId,
	validationRules,
	travelSectionSettings,
}) => {
	const { values } = useFormikContext<Record<number, any>>();
	const isAvailable = useIsAvailableSection(values, stepId, section.id);

	if (!isAvailable) {
		return null;
	}

	switch (section.type) {
		case "companion":
			return (
				<SectionWrapper color={section.backgroundColor}>
					<RegistrationCompanionSection stepId={stepId} section={section} />
				</SectionWrapper>
			);
		case "travel":
			if (!travelSectionSettings) {
				return null;
			}

			return (
				<SectionWrapper color={section.backgroundColor}>
					<RegistrationTravelSection travelSectionSettings={travelSectionSettings} />
				</SectionWrapper>
			);
		case "hotel":
			return (
				<SectionWrapper color={section.backgroundColor}>
					{section.children.map((dataFieldOrBlock) => {
						if (dataFieldOrBlock.entityType === "block") {
							return (
								<BlockRenderer
									key={`${dataFieldOrBlock.entityType}_${dataFieldOrBlock.block.id}`}
									block={dataFieldOrBlock.block}
									sectionId={section.id}
								/>
							);
						}
					})}
					<RegistrationHotelSection registrationFormId={registrationFormId} guestId={guestId} sectionId={section.id} />
				</SectionWrapper>
			);
		case "default":
			return (
				<SectionWrapper color={section.backgroundColor}>
					<RegistrationDefaultSection stepId={stepId} section={section} validationRules={validationRules} />
				</SectionWrapper>
			);
	}
};
